import { defineFont } from '@progress/kendo-drawing/pdf';

// Single source of truth for E-Letter PDF export geometry.
// Preview pagination (ELetterPreview auto page-break / bottom anchoring) and
// the Kendo drawDOM export must both derive from these values so the
// downloaded PDF matches the preview.

// Default template content width (ELetterPreview forces #pdf-data-print to
// 850px). Real templates can be wider - a content-box root of width:850px
// with padding:60px renders 970px - so scale and page capacity derive from
// the MEASURED template width, with this as the fallback.
export const TEMPLATE_CONTENT_WIDTH_PX = 850;

// A4 in PDF points and the printed page margin
const A4_WIDTH_PT = 595;
const A4_HEIGHT_PT = 842;
const PAGE_MARGIN = '5mm';
const PAGE_MARGIN_PT = 14;

const PRINTABLE_WIDTH_PT = A4_WIDTH_PT - 2 * PAGE_MARGIN_PT;
const PRINTABLE_HEIGHT_PT = A4_HEIGHT_PT - 2 * PAGE_MARGIN_PT;

// Scale that maps a template of the given width 1:1 onto the printable A4 width
export const getExportScale = (templateWidthPx?: number) =>
  Math.floor(
    (PRINTABLE_WIDTH_PT /
      Math.max(templateWidthPx || TEMPLATE_CONTENT_WIDTH_PX, 1)) *
      1000,
  ) / 1000;

// CX-5430: content pixels one exported page holds. MEASURED from Kendo, not
// derived: its DOM splitter converts the paper height to CSS px at 96dpi
// (297mm = 1122.5px) and applies NEITHER the margins NOR the export scale, so
// the window is a constant - it does not grow with the template width.
// Confirmed twice: a YESB PO (906px wide) broke exactly at 1122px, and a BSSB
// PR (972px wide) printed sum(ceil(pageHeight / 1122)) = 56 pages to the page.
// The old width-derived value (printable aspect ratio x width) overstated this
// by 15-25%, so pages the preview counted as one were split on download.
export const KENDO_PAGE_SPLIT_PX = Math.floor((A4_HEIGHT_PT * 96) / 72);

// Because that window is fixed, the scene can be pre-scaled so the window
// covers a WHOLE preview page instead of ~86% of one: normalise every
// template to this width and the window has the printable A4 aspect ratio,
// which also fills the printed sheet top to bottom (the leftover band at the
// bottom of every printed page is this mismatch, not a template problem).
export const EXPORT_SCENE_WIDTH_PX = Math.floor(
  (KENDO_PAGE_SPLIT_PX * PRINTABLE_WIDTH_PT) / PRINTABLE_HEIGHT_PT,
);

// Set false to fall back to the plain measured window: pages then hold only
// KENDO_PAGE_SPLIT_PX and printed sheets keep the bottom band.
export const PRESCALE_EXPORT_SCENE = true;

// MEASURED (AMCSB PO, drawDOM page boxes): Kendo's split window is
// min(1122px, printable height / scale) - at the pre-scaled draw scale the
// PAPER constraint binds, so the window is always one ideal page in natural
// px and KENDO_DRIFT_ALLOWANCE cancels out of the split entirely. Kendo can
// also lay a text-heavy page out up to ~8% TALLER than the browser DOM, so a
// bottom-anchored footer page packed to the planned 97% still splits its
// tail. Anchored pages therefore target this fraction of the planned page
// instead - plan-side headroom is the only kind that does not cancel.
export const ANCHOR_FIT_CUSHION = 0.93;

// One page of template pixels at the printable A4 aspect - what the scene is
// scaled against, NOT what pages are planned to (see the safety below)
const idealPageContentPx = (templateWidthPx?: number) =>
  Math.floor(
    ((templateWidthPx || TEMPLATE_CONTENT_WIDTH_PX) * PRINTABLE_HEIGHT_PT) /
      PRINTABLE_WIDTH_PT,
  );

// Factor the export PAPER is enlarged by so Kendo's split window covers one
// ideal page; 1 when the pre-scale is off. Kendo splits at
// min(px window, printable height / scale): the px window cannot be widened,
// but enlarging the paper (margins and scale scaled to match) raises the
// paper side until it binds at exactly one ideal page, and the pages are
// then shrunk back to real A4 with pdf-lib after the draw. This REPLACES
// the earlier CSS `zoom` pre-scale: Chrome's standardized zoom scales
// element rects but NOT computed font sizes, so Kendo laid glyphs at the
// raw px size into zoomed line widths and squashed every run (Tz ~= zoom) -
// downloads printed "compressed and zoomed" while all boxes measured
// correct. The 1.02 keeps the px cap safely above the paper window.
export const getExportPaperFactor = (templateWidthPx?: number) =>
  PRESCALE_EXPORT_SCENE
    ? Math.round(
        ((idealPageContentPx(templateWidthPx) * 1.02) / KENDO_PAGE_SPLIT_PX) *
          10000,
      ) / 10000
    : 1;

// MEASURED: pages planned to the FULL window land exactly on the boundary and
// spill their last element (a BSSB PR page measured 1122 zoomed px against a
// 1122 window and printed its Note block on a page of its own). Two reasons -
// the zoomed re-layout runs ~0.5% taller than the nominal zoom, and Kendo
// re-wraps text with the embedded Noto fonts, whose metrics differ from the
// template's. This must stay SEPARATE from the zoom - derive both from the
// same number and the window shrinks with the page, buying no slack at all.
// The drift is NOT a constant: it scales with how much WRAPPED TEXT a page
// carries, because re-wrapping costs whole lines. Measured per page - YESB PO
// <= 1.3%, BSSB PR <= 2.6%, and a TMS PO page (almost entirely a
// terms-and-conditions block) split with 38px of slack, so >= 3.5%.
// TRIED 0.95 for that text-heavy case and REVERTED the same day: a smaller
// plan makes a fixed-height footer page EXCEED it, and the bottom anchor then
// rounds the page up to two windows and manufactures a blank sheet. Raising
// the safety cannot help a page with no items to move anyway - only splitting
// its footer can. 0.97 leaves ~34px.
const SCENE_FIT_SAFETY = 0.97;

// Content pixels one exported page holds, in the template's own pixels
export const getPageContentHeightPx = (templateWidthPx?: number) =>
  PRESCALE_EXPORT_SCENE
    ? Math.floor(idealPageContentPx(templateWidthPx) * SCENE_FIT_SAFETY)
    : KENDO_PAGE_SPLIT_PX;

// Height the VISIBLE preview stretches a page to: the whole A4 sheet, not the
// printable area inside it. A page sized to the printable area is ~1.5%
// taller than the sheet it prints on, which reads as a stretched page next to
// the download. Content never outgrows this - a planned page is 97% of the
// printable area, which is shorter than the sheet at any template width.
export const getPreviewSheetHeightPx = (templateWidthPx?: number) =>
  Math.round(
    ((templateWidthPx || TEMPLATE_CONTENT_WIDTH_PX) * A4_HEIGHT_PT) /
      A4_WIDTH_PT,
  );

// Fixed-width equivalents for callers without a measured template width
export const EXPORT_PDF_SCALE = getExportScale(TEMPLATE_CONTENT_WIDTH_PX);
export const A4_CONTENT_HEIGHT_PX = getPageContentHeightPx(
  TEMPLATE_CONTENT_WIDTH_PX,
);
export const A4_CONTENT_WIDTH_PX = Math.floor(
  PRINTABLE_WIDTH_PT / EXPORT_PDF_SCALE,
);

// CX-5430: the Kendo PDF writer has no per-character font fallback like the
// browser - text in a family it does not know degrades to the built-in
// Helvetica (WinAnsi), which turns CJK characters into garbage glyphs. Map
// the families templates commonly use to a bundled CJK-capable font so the
// export embeds real glyphs (Kendo subsets the font per document). Latin
// text in these exports renders in Noto Sans instead of Helvetica.
let pdfFontsRegistered = false;
export const registerPdfExportFonts = () => {
  if (pdfFontsRegistered) return;
  pdfFontsRegistered = true;
  const base = `${process.env.PUBLIC_URL || ''}/fonts`;
  const regular = `${base}/NotoSansSC-Regular.ttf`;
  const bold = `${base}/NotoSansSC-Bold.ttf`;
  const families = [
    'Segoe UI',
    'Tahoma',
    'Geneva',
    'Verdana',
    'Arial',
    'Helvetica',
    'Times New Roman',
    'Microsoft YaHei',
    'SimSun',
    'SimHei',
    'sans-serif',
    'serif',
  ];
  const definitions: { [fontName: string]: string } = {};
  families.forEach(family => {
    definitions[family] = regular;
    definitions[`${family}|Bold`] = bold;
    definitions[`${family}|Italic`] = regular;
    definitions[`${family}|Bold Italic`] = bold;
  });
  defineFont(definitions);
};

// CX-5430: footer block to bottom-anchor on a page. Templates are free-form:
// some only have #ItemFooter, and some nest #PageFooter INSIDE #ContentFooter
// (last line of the block) - picking the inner one there would split the
// block, so return the OUTERMOST footer element that exists
export const findFooterBlock = (root: Element): HTMLElement | null => {
  const candidates = ['#PageFooter', '#ContentFooter', '#ItemFooter']
    .map(selector => root.querySelector(selector) as HTMLElement | null)
    .filter((el): el is HTMLElement => !!el);
  return (
    candidates.find(el =>
      candidates.every(other => other === el || !other.contains(el)),
    ) ??
    candidates[0] ??
    null
  );
};

// CX-5430: a designer-inserted <!-- pagebreak --> in the footer region moves
// everything from the marker onward onto its own printed page (e.g. notes +
// signatory after the totals). The break is resolved at the MARKER NODE, not
// a block boundary, so it works wherever the designer drops it - nested any
// depth, between sibling footer blocks (YESB closes #ContentFooter before
// #ItemFooter opens), or as a stray sibling comment. The footer region
// starts at the template's FIRST footer element in document order; markers
// before it are body markers (handled by the measure() lookup), and a
// trailing marker with no real content after it stays inert (a blank page
// helps nobody).
const nodeHasRealContent = (node: Node): boolean => {
  // a comment's textContent is its comment text - never printable content
  if (node.nodeType === Node.COMMENT_NODE) return false;
  if ((node.textContent || '').replace(/\u00a0/g, ' ').trim() !== '') {
    return true;
  }
  return node.nodeType === Node.ELEMENT_NODE
    ? !!(node as Element).querySelector('img, table')
    : false;
};

const followsOrIs = (anchor: Node, node: Node): boolean =>
  node === anchor ||
  !!(anchor.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_FOLLOWING);

export const findFooterBreakMarker = (root: Element): Comment | null => {
  const regionStart = root.querySelector(
    '#PageFooter, #ContentFooter, #ItemFooter',
  );
  if (!regionStart) return null;
  const doc = root.ownerDocument;
  if (!doc) return null;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if ((node.textContent || '').trim() !== 'pagebreak') continue;
    if (!regionStart.contains(node) && !followsOrIs(regionStart, node)) {
      continue;
    }
    // real content must follow the marker somewhere up the ancestor chain
    let cursor: Node | null = node;
    while (cursor && cursor !== root) {
      for (let sib = cursor.nextSibling; sib; sib = sib.nextSibling) {
        if (nodeHasRealContent(sib)) return node as Comment;
      }
      cursor = cursor.parentNode;
    }
  }
  return null;
};

// Split the rendered page at the footer break marker. side 'before' keeps
// the content above the marker (drops everything after it, then unwraps
// ancestors the cut emptied so no styled empty strip prints); side 'after'
// keeps the content from the marker onward (drops the footer region above
// it - the document header before the region is untouched, and the item
// table is already dropped by the footerOnlyPage handling).
export const applyFooterBreak = (
  root: Element,
  side: 'before' | 'after',
): boolean => {
  const marker = findFooterBreakMarker(root);
  if (!marker) return false;
  if (side === 'before') {
    let cursor: Node | null = marker;
    while (cursor && cursor !== root) {
      while (cursor.nextSibling) {
        cursor.nextSibling.remove();
      }
      cursor = cursor.parentNode;
    }
    let emptied = marker.parentElement;
    marker.remove();
    while (emptied && emptied !== root && !nodeHasRealContent(emptied)) {
      const parent: HTMLElement | null = emptied.parentElement;
      emptied.remove();
      emptied = parent;
    }
  } else {
    const regionStart = root.querySelector(
      '#PageFooter, #ContentFooter, #ItemFooter',
    );
    const inRegion = (node: Node) =>
      !regionStart || followsOrIs(regionStart, node);
    const strip = (container: Node): void => {
      for (const child of Array.from(container.childNodes)) {
        if (child === marker) return;
        if (
          child.nodeType === Node.ELEMENT_NODE &&
          (child as Element).contains(marker)
        ) {
          strip(child);
          return;
        }
        // a wrapper holding the region start alongside earlier content is
        // stripped inside-out so the pre-region part survives
        if (
          regionStart &&
          child.nodeType === Node.ELEMENT_NODE &&
          (child as Element).contains(regionStart)
        ) {
          const el = child as Element;
          for (const sub of Array.from(el.childNodes)) {
            if (inRegion(sub)) sub.remove();
          }
          // the region start itself lands here too (contains() is true for
          // the element itself) - emptied, it must not survive as a husk
          // that findFooterBlock resolves, starving the signatory anchor
          if (!nodeHasRealContent(el)) el.remove();
          continue;
        }
        if (inRegion(child)) child.remove();
      }
    };
    strip(root);
    // the marker's ancestors may be visually empty now (e.g. a marker placed
    // at the END of a totals-only #ContentFooter leaves an empty husk that
    // findFooterBlock would still resolve, starving the signatory anchor) -
    // unwrap them like the before side does
    let emptied = marker.parentElement;
    marker.remove();
    while (emptied && emptied !== root && !nodeHasRealContent(emptied)) {
      const parent: HTMLElement | null = emptied.parentElement;
      emptied.remove();
      emptied = parent;
    }
  }
  return true;
};

// Whether a template's footer region carries a manual page break worth
// honouring
export const templateHasFooterBreak = (
  templateHtml?: string | null,
): boolean => {
  if (!templateHtml || typeof DOMParser === 'undefined') return false;
  const doc = new DOMParser().parseFromString(templateHtml, 'text/html');
  return !!findFooterBreakMarker(doc.body);
};

// CX-5430: some templates (IAQ) draw the column headers in a SEPARATE
// #ItemLabel table above the #ItemValue row table(s). Two hand-maintained
// tables never agree on column widths, so the item columns drift from the
// header. Copy the header table's declared column widths onto every item
// table as a shared colgroup; when the item row has FEWER cells than the
// header (blank spacer columns merged away), spacer widths are summed into
// the preceding real column so the boundaries still coincide.
const resolveTable = (el: Element | null): HTMLTableElement | null => {
  if (!el) return null;
  if (el.tagName === 'TABLE') return el as HTMLTableElement;
  return (el.closest('table') ??
    el.querySelector('table')) as HTMLTableElement | null;
};

const rowCells = (row: Element): HTMLTableCellElement[] =>
  Array.from(row.children).filter(
    (c): c is HTMLTableCellElement =>
      c.tagName === 'TD' || c.tagName === 'TH',
  );

// Declared column widths, percent units only: colgroup first, else the
// first row's cell widths. Null when any column is unparseable or spanned.
const declaredColumnWidths = (table: HTMLTableElement): number[] | null => {
  const cols = Array.from(
    table.querySelector('colgroup')?.querySelectorAll('col') ?? [],
  );
  let sources: string[];
  if (cols.length > 0) {
    sources = cols.map(c => (c as HTMLElement).style.width || '');
  } else {
    const firstRow = table.querySelector('tr');
    if (!firstRow) return null;
    const cells = rowCells(firstRow);
    if (cells.some(c => c.colSpan > 1)) return null;
    sources = cells.map(c => c.style.width || '');
  }
  const widths = sources.map(w =>
    /^\d*\.?\d+%$/.test(w.trim()) ? parseFloat(w) : NaN,
  );
  if (widths.length === 0 || widths.some(w => !isFinite(w) || w <= 0)) {
    return null;
  }
  return widths;
};

// Sum each blank spacer column's width into the preceding real column
const mergeSpacerWidths = (
  headerTable: HTMLTableElement,
  widths: number[],
): number[] | null => {
  const headerRow = headerTable.querySelector('tr');
  if (!headerRow) return null;
  const cells = rowCells(headerRow);
  if (cells.length !== widths.length || cells.some(c => c.colSpan > 1)) {
    return null;
  }
  const groups: number[] = [];
  cells.forEach((cell, i) => {
    const blank =
      (cell.textContent || '').replace(/\u00a0/g, ' ').trim() === '';
    if (!blank || groups.length === 0) groups.push(widths[i]);
    else groups[groups.length - 1] += widths[i];
  });
  return groups;
};

export const alignSeparateItemTables = (root: HTMLElement) => {
  const labelTable = resolveTable(root.querySelector('#ItemLabel'));
  const itemTables = Array.from(root.querySelectorAll('[id="ItemValue"]'))
    .map(el => resolveTable(el))
    .filter(
      (t, i, arr): t is HTMLTableElement => !!t && arr.indexOf(t) === i,
    );
  if (itemTables.length === 0) return;

  // extractItemValue clones the #ItemValue table's innerHTML once per item
  // row - when the template keeps its colgroup INSIDE that table (BCSB),
  // every clone re-adds it and the columns accumulate: 3 items = 3
  // colgroups = 21 columns whose percentages sum to 300%, scaled down so
  // the 7 real columns render at a third of their width (crushed rows).
  // Keep only the first colgroup.
  itemTables.forEach(table => {
    Array.from(table.children)
      .filter(c => c.tagName === 'COLGROUP')
      .slice(1)
      .forEach(cg => cg.remove());
  });

  const syncedTables: Array<{
    table: HTMLTableElement;
    target: number[];
  }> = [];

  // Item tables NOT driven by a header table but by their OWN colgroup +
  // fixed layout (BCSB: empty #ItemLabel, anonymous header table, item
  // cells without widths): mirror the colgroup onto the row cells so the
  // widths survive engines that only honour cell widths - a colgroup-only
  // fixed table rendered without it falls to min-content (crushed columns)
  const stampOwnColgroup = (table: HTMLTableElement) => {
    if (table.style.tableLayout !== 'fixed') return;
    if (!table.querySelector('colgroup')) return;
    const widths = declaredColumnWidths(table);
    if (!widths) return;
    Array.from(table.querySelectorAll('tr')).forEach(tr => {
      const cells = rowCells(tr);
      if (cells.length !== widths.length) return;
      cells.forEach((cell, i) => {
        cell.style.width = `${widths[i]}%`;
      });
    });
  };

  // single-table templates (header rows and #ItemValue in one table) align
  // themselves - only the separate-table shape needs the sync
  const separateShape =
    !!labelTable &&
    !itemTables.some(t => t === labelTable || labelTable.contains(t));
  if (!separateShape) {
    itemTables.forEach(stampOwnColgroup);
    return;
  }

  const rawWidths = declaredColumnWidths(labelTable!);
  if (!rawWidths) {
    itemTables.forEach(stampOwnColgroup);
    return;
  }
  const mergedWidths = mergeSpacerWidths(labelTable!, rawWidths);

  itemTables.forEach(table => {
    const firstRow = table.querySelector('tr');
    const cellCount = firstRow ? rowCells(firstRow).length : 0;
    const target =
      cellCount === rawWidths.length
        ? rawWidths
        : mergedWidths && cellCount === mergedWidths.length
        ? mergedWidths
        : null;
    if (!target) {
      stampOwnColgroup(table);
      return;
    }
    table.querySelectorAll('colgroup').forEach(cg => cg.remove());
    const colgroup = table.ownerDocument!.createElement('colgroup');
    target.forEach(w => {
      const col = table.ownerDocument!.createElement('col');
      col.style.width = `${w}%`;
      colgroup.appendChild(col);
    });
    table.insertBefore(colgroup, table.firstChild);
    table.style.tableLayout = 'fixed';
    // Chrome resolves a col-vs-cell width conflict in the cells' favour,
    // so the template's own td widths would still win - stamp the target
    // widths on every same-shaped row so both sources agree
    Array.from(table.querySelectorAll('tr')).forEach(tr => {
      const cells = rowCells(tr);
      if (cells.length !== target.length) return;
      cells.forEach((cell, i) => {
        cell.style.width = `${target[i]}%`;
      });
    });
    syncedTables.push({ table, target });
  });
  // fixed layout makes the colgroup authoritative on both sides
  if (syncedTables.length > 0) labelTable!.style.tableLayout = 'fixed';

  // With exactly one synced item table, fold the header row INTO it as a
  // <thead>: two stacked tables draw two outer frames with a seam at the
  // junction in the Kendo redraw, while one table is one collapsed grid -
  // and Kendo repeatHeaders can then repeat the real column header on
  // physical page splits
  const labelRows = Array.from(labelTable!.querySelectorAll('tr'));
  const single = syncedTables.length === 1 ? syncedTables[0] : null;
  if (
    single &&
    labelRows.length === 1 &&
    !single.table.querySelector('thead')
  ) {
    const headerRow = labelRows[0];
    if (single.target === mergedWidths) {
      // drop the blank spacer cells whose widths were merged away
      let seenReal = false;
      rowCells(headerRow).forEach(cell => {
        const isBlank =
          (cell.textContent || '').replace(/\u00a0/g, ' ').trim() === '';
        if (isBlank && seenReal) cell.remove();
        else seenReal = true;
      });
    }
    const headerCells = rowCells(headerRow);
    if (headerCells.length === single.target.length) {
      headerCells.forEach((cell, i) => {
        cell.style.width = `${single.target[i]}%`;
      });
      // the label TABLE may carry the header frame itself (ACSB:
      // border: 1px solid on the table element) - push it into the row
      // cells before the table is dropped, or the frame is lost
      resolveTableFrame(labelTable!, labelTable!.hasAttribute('border'));
      labelTable!.removeAttribute('border');
      const thead = single.table.ownerDocument!.createElement('thead');
      thead.appendChild(headerRow);
      single.table.insertBefore(thead, single.table.querySelector('tbody'));
      labelTable!.remove();
    }
  }
};

// CX-5430: complete half-declared cell borders on the item grid. A table
// border attribute or a style missing its width/style/color longhands is
// resolved by the browser to a soft inset default, but Kendo's PDF writer
// resolves it itself and draws dark solid lines (IAQ "black border"). Give
// every item/header-grid cell an explicit width+style+color instead.
export const completeItemTableBorders = (root: HTMLElement) => {
  const tables: HTMLTableElement[] = [];
  const push = (t: HTMLTableElement | null) => {
    if (t && tables.indexOf(t) === -1) tables.push(t);
  };
  push(resolveTable(root.querySelector('#ItemLabel')));
  root
    .querySelectorAll('[id="ItemValue"]')
    .forEach(el => push(resolveTable(el)));

  const SIDES = ['top', 'right', 'bottom', 'left'];
  tables.forEach(table => {
    const hadBorderAttr = table.hasAttribute('border');
    if (hadBorderAttr) table.removeAttribute('border');
    Array.from(table.querySelectorAll('th, td')).forEach(cellEl => {
      const s = (cellEl as HTMLTableCellElement).style;
      const anyDeclared = SIDES.some(
        side =>
          s.getPropertyValue(`border-${side}-width`) !== '' ||
          s.getPropertyValue(`border-${side}-style`) !== '' ||
          s.getPropertyValue(`border-${side}-color`) !== '',
      );
      if (!anyDeclared) {
        // no declaration at all - only the attribute border implied a grid
        if (hadBorderAttr) s.border = '1px solid #cccccc';
        return;
      }
      // complete per side so undeclared sides never gain a border (an
      // unset side width defaults to medium once a style appears)
      SIDES.forEach(side => {
        const width = s.getPropertyValue(`border-${side}-width`);
        const style = s.getPropertyValue(`border-${side}-style`);
        const hasWidth = width !== '' && parseFloat(width) !== 0;
        const hasStyle = style !== '' && style !== 'none' && style !== 'hidden';
        if (!hasWidth && !hasStyle) return;
        if (hasWidth && style === '') {
          s.setProperty(`border-${side}-style`, 'solid');
        }
        if (hasStyle && width === '') {
          s.setProperty(`border-${side}-width`, '1px');
        }
        if (s.getPropertyValue(`border-${side}-color`) === '') {
          s.setProperty(`border-${side}-color`, '#cccccc');
        }
      });
    });
    emulateCollapsedBorders(table);
    resolveTableFrame(table, hadBorderAttr);
  });
};

// The table element's OWN border (e.g. YESB: `border-style: solid;
// border-color` with the width coming from border="1") must not be left
// half-declared either: stripping the attribute would make the solid style
// fall back to the 3px `medium` default, and Kendo would draw the frame on
// top of the edge cells' borders. Resolve the frame the way collapse would
// - push it into the edge cells where they are thinner - then zero it on
// the table so exactly one engine-safe frame remains.
const resolveTableFrame = (
  table: HTMLTableElement,
  hadBorderAttr: boolean,
) => {
  const s = table.style;
  const declared = ['top', 'right', 'bottom', 'left'].some(
    side =>
      s.getPropertyValue(`border-${side}-width`) !== '' ||
      s.getPropertyValue(`border-${side}-style`) !== '',
  );
  if (!declared && !hadBorderAttr) return;

  const rows = Array.from(table.querySelectorAll('tr'))
    .map(tr => rowCells(tr))
    .filter(cells => cells.length > 0);
  if (rows.length === 0) return;

  const edgeCells = (side: string): HTMLTableCellElement[] => {
    if (side === 'top') return rows[0];
    if (side === 'bottom') return rows[rows.length - 1];
    return rows.map(cells =>
      side === 'left' ? cells[0] : cells[cells.length - 1],
    );
  };

  ['top', 'right', 'bottom', 'left'].forEach(side => {
    const width = s.getPropertyValue(`border-${side}-width`);
    const style = s.getPropertyValue(`border-${side}-style`);
    const hasStyle = style !== '' && style !== 'none' && style !== 'hidden';
    const intended =
      width !== ''
        ? hasStyle || hadBorderAttr
          ? borderWidthPx(width)
          : 0
        : hasStyle || hadBorderAttr
        ? 1
        : 0;
    if (intended > 0) {
      const frameColor =
        s.getPropertyValue(`border-${side}-color`) ||
        s.getPropertyValue('border-color') ||
        '#cccccc';
      edgeCells(side).forEach(cell => {
        const cs = cell.style;
        if (borderWidthPx(cs.getPropertyValue(`border-${side}-width`)) >= intended) {
          return;
        }
        cs.setProperty(`border-${side}-width`, `${intended}px`);
        const cellStyle = cs.getPropertyValue(`border-${side}-style`);
        if (cellStyle === '' || cellStyle === 'none') {
          cs.setProperty(`border-${side}-style`, 'solid');
        }
        if (cs.getPropertyValue(`border-${side}-color`) === '') {
          cs.setProperty(`border-${side}-color`, frameColor);
        }
      });
    }
  });
  s.setProperty('border-width', '0px');
};

// The browser merges adjacent cell borders under border-collapse: collapse,
// but Kendo's PDF writer draws every cell's borders separately and applies
// the default 2px border-spacing - each shared edge becomes TWO grey lines
// with a gap (IAQ "doubled border"). Emulate the collapse in the DOM:
// resolve each shared edge once, keep it on the first cell and zero the
// neighbour's side. The browser output is unchanged (collapse resolves to
// the same single line); Kendo is left with exactly one line per edge.
const borderWidthPx = (value: string): number => {
  if (value === 'thin') return 1;
  if (value === 'medium') return 3;
  if (value === 'thick') return 5;
  const n = parseFloat(value);
  return isFinite(n) ? n : 0;
};

const emulateCollapsedBorders = (table: HTMLTableElement) => {
  if (table.style.borderCollapse !== 'collapse') return;
  table.style.borderSpacing = '0';

  // keep the winning side on cell A, zero the same edge on cell B
  const resolveEdge = (
    a: HTMLTableCellElement,
    aSide: string,
    b: HTMLTableCellElement,
    bSide: string,
  ) => {
    const aw = borderWidthPx(a.style.getPropertyValue(`border-${aSide}-width`));
    const bw = borderWidthPx(b.style.getPropertyValue(`border-${bSide}-width`));
    if (aw === 0 && bw === 0) return;
    if (bw > aw) {
      a.style.setProperty(`border-${aSide}-width`, `${bw}px`);
      const bStyle = b.style.getPropertyValue(`border-${bSide}-style`);
      const bColor = b.style.getPropertyValue(`border-${bSide}-color`);
      if (bStyle) a.style.setProperty(`border-${aSide}-style`, bStyle);
      if (bColor) a.style.setProperty(`border-${aSide}-color`, bColor);
    }
    b.style.setProperty(`border-${bSide}-width`, '0px');
  };

  const rows = Array.from(table.querySelectorAll('tr')).map(tr =>
    rowCells(tr),
  );
  rows.forEach((cells, r) => {
    // vertical edges within the row
    for (let c = 1; c < cells.length; c++) {
      resolveEdge(cells[c - 1], 'right', cells[c], 'left');
    }
    // horizontal edges against the previous same-shaped row
    const prev = rows[r - 1];
    if (r > 0 && prev && prev.length === cells.length) {
      cells.forEach((cell, c) => resolveEdge(prev[c], 'bottom', cell, 'top'));
    }
  });
};

export const buildELetterDrawOptions = (templateWidthPx?: number) => {
  const paperFactor = getExportPaperFactor(templateWidthPx);
  const width = Math.max(templateWidthPx || TEMPLATE_CONTENT_WIDTH_PX, 1);
  const margin =
    paperFactor === 1
      ? PAGE_MARGIN
      : `${Math.round(PAGE_MARGIN_PT * paperFactor * 100) / 100}pt`;
  return {
    paperSize:
      paperFactor === 1
        ? ('A4' as const)
        : ([
            Math.round(A4_WIDTH_PT * paperFactor * 100) / 100,
            Math.round(A4_HEIGHT_PT * paperFactor * 100) / 100,
          ] as [number, number]),
    scale:
      Math.floor(((PRINTABLE_WIDTH_PT * paperFactor) / width) * 1000) / 1000,
    margin: { top: margin, bottom: margin, left: margin, right: margin },
    forcePageBreak: '.page-break',
    repeatHeaders: true,
  };
};
